import { useCallback, useState } from 'react';
/**
 * Manages a set of indices that need to wrap or be contained.
 *
 * @example
 *
 * const {
 *   activeIndex,
 *   moveActiveIndex,
 * } = useRovingIndex({ maxIndex: items.length - 1 })
 *
 * @param {{
 *   maxIndex: number,
 *   defaultIndex?: number,
 *   wrap?: boolean
 * }} [options]
 * @returns {{
 *   activeIndex: number,
 *   moveActiveIndex: (amountToMove: number) => void,
 *   setActiveIndex: (nextIndex: number) => void,
 *   moveBackwardDisabled: boolean,
 *   moveForwardDisabled: boolean,
 * }}
 */

export function useRovingIndex(_ref) {
  var maxIndex = _ref.maxIndex,
      _ref$defaultIndex = _ref.defaultIndex,
      defaultIndex = _ref$defaultIndex === void 0 ? 0 : _ref$defaultIndex,
      _ref$wrap = _ref.wrap,
      wrap = _ref$wrap === void 0 ? false : _ref$wrap;

  var _useState = useState(defaultIndex),
      activeIndex = _useState[0],
      setActiveIndex = _useState[1];

  var getWrappedIndex = useCallback(function (nextIndex) {
    return wrap ? (nextIndex % maxIndex + maxIndex) % maxIndex : nextIndex > maxIndex ? maxIndex : nextIndex < 0 ? 0 : nextIndex;
  }, [maxIndex, wrap]);
  return {
    activeIndex: activeIndex,
    moveActiveIndex: useCallback(function (amountToMove) {
      setActiveIndex(function (currentIndex) {
        return getWrappedIndex(currentIndex + amountToMove);
      });
    }, [getWrappedIndex]),
    setActiveIndex: useCallback(function (nextIndex) {
      setActiveIndex(getWrappedIndex(nextIndex));
    }, [getWrappedIndex]),
    moveBackwardDisabled: activeIndex <= 0,
    moveForwardDisabled: activeIndex >= maxIndex
  };
}